import React, { useState } from 'react';
import type { PropsWithChildren } from 'react';

import ConsentAlert from '@components/Alert/Type/ConsentAlert';
import ContentLoader from '@components/ContentLoader';
import CurationBanner from '@components/CurationBanner';
import DesktopHeader from '@components/DesktopHeader';
import Icon from '@components/Icon/Icon';
import MobileBottomMenu from '@components/MobileBottomMenu';
import type { OptionKeys } from '@components/MobileBottomMenu/MobileBottomMenu';
import MobileSideMenu from '@components/MobileSideMenu';
import type { IMenuLink } from '@components/MobileSideMenu/MobileSideMenu';
import SideStripDesktop from '@components/SideStripDesktop';
import SpeedDial from '@components/SpeedDial';

import { RECORD_UPLOAD } from '@constants/record-update-types';
import { RECORD_UPDATE_TYPES } from '@constants/record-update-types';

import { useCheckConsent } from '@hooks/useCheckConsent';
import { useCuration } from '@hooks/useCuration';
// import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useDrawer } from '@hooks/useDrawer';
import { useIsMobile } from '@hooks/useIsMobile';

import type { LayoutSetting } from '@layouts/AppLayout/model';

import {
  AppContent,
  Hamburger,
  MobileHeaderBar,
  MobileWrapper,
} from './AppLayout.style';

interface BaseLayoutProps extends PropsWithChildren {
  children: React.ReactNode;
  friendlyName?: string | null;
  loading?: boolean;
  showHeader?: boolean;
  settings: {
    desktop: LayoutSetting;
    mobile: readonly (readonly OptionKeys[])[];
  };
  links?: IMenuLink[];
  showSpeedDial?: boolean;
}

const USER_FAB_SETTINGS_MOBILE = [
  RECORD_UPDATE_TYPES.RECORD_UPDATE,
  RECORD_UPDATE_TYPES.MISSING_INFORMATION,
  RECORD_UPDATE_TYPES.INCORRECT_INFORMATION,
  RECORD_UPLOAD,
] as const;

export default function BaseLayout({
  children,
  friendlyName,
  links,
  loading: baseLoading,
  settings,
  showHeader,
  showSpeedDial,
}: BaseLayoutProps) {
  const [openMobileDrawer, setOpenMobileDrawer] = useState(false);
  const mobile = useIsMobile();
  const {
    data: { isUnconsentingPatient },
    loading: consentLoading,
  } = useCheckConsent();
  const {
    state: { patientView },
  } = useCuration();
  const {
    state: { isOpen },
  } = useDrawer();
  /*
  const { flags } = useFeatureFlags();
  const {
    isAllergyIntoleranceCreateAllergyEnabled,
    isAppointmentCreateAppointmentEnabled,
    isBodyCreateObservationEnabled,
    isConditionCreateConditionEnabled,
    isCoverageCreateCoverageEnabled,
    isEmergencyVisitCreateEncounterEnabled,
    isFamilyMemberHistoryCreateFamilyMemberHistoryEnabled,
    isGeneticCreateObservationEnabled,
    isHospitalizationCreateEncounterEnabled,
    isImagingTestCreateDiagnosticReportEnabled,
    isImmunizationCreateImmunizationEnabled,
    isLabReportCreateDiagnosticReportEnabled,
    isMedicationCreateMedicationStatementEnabled,
    isProcedureCreateProcedureEnabled,
    isVitalSignCreateObservationEnabled,
  } = flags;
   */

  const loading = baseLoading || consentLoading;

  const showCurationBanner = patientView;

  return mobile ? (
    <>
      {isUnconsentingPatient ? <ConsentAlert /> : null}
      <MobileSideMenu
        isOpen={openMobileDrawer}
        links={links}
        onClose={() => setOpenMobileDrawer(false)}
      />
      {showHeader ? (
        <MobileHeaderBar>
          <Hamburger onClick={() => setOpenMobileDrawer(true)}>
            <Icon color="text.primary" name="bars" type="fal" />
          </Hamburger>
          {/* TODO
              <NotificationBell
                onClick={() => {
                  navigate(ROUTES.NOTIFICATIONS);
                }}
              /> */}
        </MobileHeaderBar>
      ) : null}
      <MobileWrapper>
        <ContentLoader loading={loading}>{children}</ContentLoader>
      </MobileWrapper>
      {showSpeedDial ? <SpeedDial settings={USER_FAB_SETTINGS_MOBILE} /> : null}
      <MobileBottomMenu settings={settings.mobile} />
    </>
  ) : (
    <>
      <SideStripDesktop settings={settings.desktop} />
      <DesktopHeader friendlyName={friendlyName} />
      {showCurationBanner ? <CurationBanner /> : null}
      <AppContent drawerOpen={isOpen} showBanner={showCurationBanner}>
        <ContentLoader loading={loading}>
          {isUnconsentingPatient ? <ConsentAlert /> : null}
          {children}
        </ContentLoader>
      </AppContent>
    </>
  );
}
